import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getWarehouseListAsync } from "src/redux/actions/omsPageActions";
import { useAppDispatch } from "src/redux/storeHooks";

interface Warehouse {
  id: number;
  api_name: string;
}

interface LocationModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (selected: { id: number; api_name: string }[]) => void;
}

const LocationModal: React.FC<LocationModalProps> = ({
  show,
  onClose,
  onSave,
}) => {
  const dispatch = useAppDispatch();

  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const { loading, warehouseList } = useSelector(
    (state: any) => state.warehouseSlice
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!show) return;

    setPage(1);
    setHasNextPage(true);
    setSelected(new Set());
  }, [show]);

  useEffect(() => {
    if (!show) return;
    if (!hasNextPage && page !== 1) return;

    dispatch(
      getWarehouseListAsync({
        paramData: {
          page,
          limit: 20,
          search: debouncedSearch,
          sort_by: "id",
          sort_order: "asc",
          warehouse_id: null,
          isActive: true,
        },
      })
    ).then((res: any) => {
      const payload = res?.payload;
      setHasNextPage(payload?.next !== null);
    });
  }, [page, debouncedSearch, show]);

  const handleScroll = (): void => {
    const el = bodyRef.current;
    if (!el || loading || !hasNextPage) return;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      setPage((prev) => prev + 1);
    }
  };

  const toggleSelect = (id: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1}>
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "500px", width: "100%" }} // Fixed width
        >
          <div className="modal-content" style={{ height: "400px" }}> {/* Fixed height */}
            {/* Header */}
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="modal-title mb-0">Edit locations</h5>
              <div className="d-flex gap-2">
                <button className="btn btn-light btn-sm" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() =>
                    onSave(
                      warehouseList
                        .filter((w: Warehouse) => selected.has(w.id))
                        .map((w: Warehouse) => ({
                          id: w.id,
                          api_name: w.api_name,
                        }))
                    )
                  }
                >
                  Save
                </button>
              </div>
            </div>

            {/* Body */}
            <div
              ref={bodyRef}
              onScroll={handleScroll}
              className="modal-body p-0"
              style={{ maxHeight: "calc(100% - 56px)", overflowY: "auto" }}
            >
              <div className="d-flex flex-column gap-4 w-100 p-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search locations"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {warehouseList?.map((item: Warehouse) => (
                  <label
                    key={item.id}
                    className="d-flex align-items-center gap-2 border-bottom pb-2"
                  >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selected.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                    />
                    <span>{item.api_name}</span>
                  </label>
                ))}

                {loading && (
                  <div className="text-center text-muted small">Loading...</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default LocationModal;
